import React from 'react';
import { Crown, Heart, Shield, Users, Sparkles, Globe, CheckCircle, Instagram, Twitter, Youtube, Globe2, ExternalLink, Star, Music, Mic2, Drama, Bot, User as UserIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import JsonLd from '../components/JsonLd';

const About = () => {
  // Schema.org JSON-LD for About Page - Maximum SEO
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About PopCrown - Michael Jackson Fan Community",
    "description": "Learn about PopCrown, the ultimate unofficial Michael Jackson fan community. Built by fans, for fans. A safe space for MJ lovers, King of Pop enthusiasts, Jackson 5 fans to connect and celebrate.",
    "url": "https://popcrownofficial.com/about",
    "mainEntity": {
      "@type": "Organization",
      "name": "PopCrown Official",
      "alternateName": ["PopCrown", "PopCrown Social", "PopCrown MJ Community"],
      "description": "Unofficial Michael Jackson fan community - a safe, moderated space for MJ fans worldwide to connect, share memories, and celebrate the King of Pop legacy",
      "url": "https://popcrownofficial.com",
      "foundingDate": "2024",
      "knowsAbout": ["Michael Jackson", "King of Pop", "Jackson 5", "Thriller", "Bad", "Dangerous", "HIStory", "Pop Music", "Music History"]
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://popcrownofficial.com"},
      {"@type": "ListItem", "position": 2, "name": "About", "item": "https://popcrownofficial.com/about"}
    ]
  };

  const values = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Fan First',
      description: 'Everything we build starts with the fan experience. Your passion drives our purpose.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Safe Spaces',
      description: 'Dedicated fan communities built on respect, passion, and real conversation.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'True Connection',
      description: 'Real conversations with people who share your passion and respect your love for the art.'
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: 'Truth Matters',
      description: 'We protect truth, moderate firmly, and keep standards high for the community.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="About PopCrown — The Team Behind the World's Biggest MJ Fan Community"
        description="Meet Tasha and Jo, the co-founders of PopCrown. Built by fans, for fans. Learn how PopCrown became the #1 Michael Jackson fan community with members in 130+ countries."
        keywords="about PopCrown, PopCrown co-founders, Tasha PopCrown, Jo PopCrown, Michael Jackson fan community, MJ fan app, King of Pop community, PopCrown team"
        canonical="/about"
        schema={[{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://popcrownofficial.com/"},
            {"@type": "ListItem", "position": 2, "name": "About", "item": "https://popcrownofficial.com/about"}
          ]
        }, {
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Tasha",
          "jobTitle": "Co-Founder",
          "worksFor": {"@type": "Organization", "name": "PopCrown Official", "url": "https://popcrownofficial.com"},
          "knowsAbout": ["Michael Jackson", "fan community management", "content creation"]
        }, {
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Jo",
          "jobTitle": "Co-Founder",
          "worksFor": {"@type": "Organization", "name": "PopCrown Official", "url": "https://popcrownofficial.com"},
          "knowsAbout": ["Michael Jackson", "app development", "community building"]
        }]}
      />
      {/* Schema.org JSON-LD for About Page */}
      <JsonLd data={aboutSchema} />
      <JsonLd data={breadcrumbSchema} />

      {/* Hero Section */}
      <section className="relative py-12 overflow-hidden">
        {/* Background Image - Concert Crowd - SEO Optimized */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=2070)',
          }}
          role="img"
          aria-label="Concert crowd with colorful lights celebrating music like Michael Jackson fans at legendary King of Pop performances worldwide"
        />
        {/* Hidden img for SEO */}
        <img 
          src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=2070"
          alt="Concert crowd celebrating with colorful stage lights - Michael Jackson fan community like legendary King of Pop Bad World Tour Dangerous Tour HIStory Tour This Is It concerts - MJ fans united at PopCrown celebrating Thriller Beat It Billie Jean music legacy"
          className="sr-only"
          loading="eager"
        />
        {/* Overlay */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.4) 0%, rgba(0, 0, 0, 0.7) 50%, rgba(225, 6, 0, 0.3) 100%)'
        }} />
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-16 h-16 mx-auto mb-6 crown-pulse object-contain" />
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            <span className="thriller-text">About PopCrown — Michael Jackson Fan Community</span>
          </h1>
          <p className="text-2xl md:text-3xl text-white mb-6">
            The world's only Michael Jackson fan app. Where fandom feels like home.
          </p>
          <p className="text-xl md:text-2xl text-gray-200 mb-4 max-w-3xl mx-auto italic" style={{
            fontFamily: "'Great Vibes', cursive",
            lineHeight: '1.6'
          }}>
            <span className="text-[#E10600]">"</span>
            Built by fans, for fans, with respect and truth at the centre.
            <span className="text-[#E10600]">"</span>
          </p>
          <div className="mx-auto mt-6 h-1 w-32 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="py-12" style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 100%)'
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                <strong className="text-[#DC143C]">PopCrown Social</strong> is the world's only dedicated Michael Jackson fan app — plus events brand, 
                YouTube channel, podcast, and online community — created to give Michael Jackson fans a better place to belong. No other app in the world is built exclusively for the King of Pop's fanbase.
              </p>
              
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Social platforms can be brilliant for finding other Michael Jackson fans, but they can also be loud, 
                chaotic, and full of negativity. We built PopCrown to be different: a <strong>positive, well-moderated space</strong> where 
                Michael Jackson fans can connect, share memories, celebrate the King of Pop's iconic moments, and enjoy a community built on 
                respect, passion, and real conversation — without trolls or tabloid nonsense.
              </p>

              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Michael Jackson's legacy spans generations and reaches every corner of the world. His fans are among the most passionate, 
                dedicated, and misunderstood communities online. We created PopCrown because Michael Jackson fans deserve a calm, 
                respectful place to discuss his music, share memories of the King of Pop, and participate without every conversation being derailed by hate, misinformation, or bad-faith attacks.
              </p>

              <p className="text-gray-700 text-lg leading-relaxed">
                <strong className="text-[#DC143C]">PopCrown Official Ltd</strong> is the registered company name for this independent fan community. Not affiliated with, 
                endorsed by, or connected to any artists, the Michael Jackson Estate, or any rights holders.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-12" style={{
        background: 'linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 100%)'
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
                Our Story, and Why <span className="thriller-text">Truth Matters</span>
              </h2>
              <div className="mx-auto h-1 w-24 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                PopCrown was created because we believe truth deserves protecting, especially online. In big public spaces, 
                misinformation spreads fast, bad faith takes over, and the loudest voices can drown out the real fans. 
                We are building a home for people who want to celebrate an artist's work and legacy with respect, care, and honesty.
              </p>

              {/* Highlight Box */}
              <div className="p-6 rounded-xl my-8" style={{
                background: 'linear-gradient(135deg, rgba(225, 6, 0, 0.1) 0%, rgba(220, 20, 60, 0.1) 100%)',
                borderLeft: '4px solid #E10600'
              }}>
                <p className="text-gray-800 text-xl font-medium mb-0">
                  That does not mean everyone has to agree on everything. It means we do not allow harassment, vulgarity, 
                  or false claims presented as fact. We moderate firmly, we keep standards high, and we put the community first — 
                  so the stories, memories, and history fans share can be preserved in a way that feels safe and meaningful.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-12 relative" style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 50%, #FFFFFF 100%)'
      }}>
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 25% 25%, #E10600 0%, transparent 50%), radial-gradient(circle at 75% 75%, #DC143C 0%, transparent 50%)'
        }} />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
              Our <span className="thriller-text">Values</span>
            </h2>
            <p className="text-gray-600 text-lg">The principles that guide everything we do</p>
            <div className="mx-auto mt-4 h-1 w-24 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {values.map((value, index) => (
              <div 
                key={index}
                className="p-6 rounded-xl text-center transition-all hover:transform hover:scale-105"
                style={{
                  background: '#FFFFFF',
                  border: '2px solid rgba(225, 6, 0, 0.3)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                }}
              >
                <div className="text-[#DC143C] mb-4 flex justify-center">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-black mb-2">{value.title}</h3>
                <p className="text-gray-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Vision Section */}
      <section className="py-12" style={{
        background: 'linear-gradient(180deg, #1a1a1a 0%, #2a2a2a 100%)'
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Globe className="w-16 h-16 text-[#E10600] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              The <span className="thriller-text">Future</span>
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              More PopCrown communities are planned for 2026, each built around an iconic artist and guided 
              by the fans who live and breathe the legacy. If you want a fan space that feels uplifting, 
              respectful, and genuinely welcoming, you're in the right place.
            </p>
            
            {/* Youth & Community Projects */}
            <div className="p-6 rounded-xl mb-8 text-left" style={{
              background: 'linear-gradient(135deg, rgba(225, 6, 0, 0.1) 0%, rgba(220, 20, 60, 0.05) 100%)',
              border: '2px solid rgba(225, 6, 0, 0.2)'
            }}>
              <h3 className="text-xl font-bold text-[#E10600] mb-4 text-center inline-flex items-center gap-2 w-full justify-center">
                <Star className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" />
                Supporting the Next Generation
              </h3>
              <p className="text-gray-300 mb-4">
                PopCrown was built by fans who believe pop culture can do more than entertain. Music, dance, and performance 
                have inspired millions of young people to dream bigger, and we want to turn that inspiration into real world opportunity.
              </p>
              <p className="text-gray-300 mb-4">
                That is why our giving is community led. As our donation fund grows, we will invite local community groups 
                such as youth clubs, grassroots organisations, and creative arts programmes to tell us what they would do with funding. 
                Then our members vote, and the recipient with the most votes receives the donation for that round.
              </p>
              <p className="text-gray-300 mb-4">
                When the fund reaches £5,000, we will run our first community vote. T&Cs apply.
              </p>
              <p className="text-gray-300 mb-4">
                Examples of the kinds of projects we aim to support include:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-6 h-6 text-[#E10600] mt-1 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                  <div>
                    <p className="text-white font-semibold">Dance Programmes</p>
                    <p className="text-gray-400 text-sm">Youth dance classes, workshops, and performance opportunities</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Music className="w-6 h-6 text-[#E10600] mt-1 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                  <div>
                    <p className="text-white font-semibold">Musical Tuition</p>
                    <p className="text-gray-400 text-sm">Music lessons and access to instruments for underprivileged children</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mic2 className="w-6 h-6 text-[#E10600] mt-1 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                  <div>
                    <p className="text-white font-semibold">Vocal Coaching</p>
                    <p className="text-gray-400 text-sm">Singing lessons and vocal development programmes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Drama className="w-6 h-6 text-[#E10600] mt-1 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                  <div>
                    <p className="text-white font-semibold">Performing Arts Projects</p>
                    <p className="text-gray-400 text-sm">Theatre, creative expression, and stage performance opportunities</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="inline-block p-6 rounded-xl" style={{
              background: 'linear-gradient(135deg, rgba(225, 6, 0, 0.1) 0%, rgba(220, 20, 60, 0.1) 100%)',
              border: '2px solid rgba(225, 6, 0, 0.3)'
            }}>
              <p className="text-white text-xl font-semibold mb-2">Coming 2026</p>
              <p className="text-[#E10600]">New artist communities • Youth arts programmes • Global expansion • More ways to give back</p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet The Team Section */}
      <section className="py-12" style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF8F8 100%)'
      }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-12 h-12 mx-auto mb-4 object-contain" />
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
              Meet The <span className="thriller-text">Team</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              The passionate fans behind PopCrown who are building something special for our community.
            </p>
            <div className="mx-auto mt-4 h-1 w-24 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
          </div>

          {/* Co-Founders */}
          <div className="max-w-4xl mx-auto mb-16">
            <h3 className="text-2xl font-bold text-center text-[#DC143C] mb-8">Co-Founders</h3>
            
            {/* Photos side by side */}
            <div className="flex justify-center gap-8 md:gap-16 mb-8">
              {/* Jo */}
              <div className="text-center">
                <div className="w-32 h-32 md:w-40 md:h-40 mx-auto mb-4 rounded-full overflow-hidden" style={{
                  background: 'linear-gradient(135deg, #E10600 0%, #DC143C 100%)',
                  padding: '3px'
                }}>
                  <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_mjlegalvault-1/artifacts/mxlhupu9_jo.PNG" 
                      alt="Jo - PopCrown Co-Founder - Michael Jackson fan community leader - MJ fan advocate"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <h4 className="text-xl font-bold text-black">Jo</h4>
                <p className="text-[#DC143C] font-semibold text-sm">Co-Founder</p>
              </div>

              {/* Tasha */}
              <div className="text-center">
                <div className="w-32 h-32 md:w-40 md:h-40 mx-auto mb-4 rounded-full overflow-hidden" style={{
                  background: 'linear-gradient(135deg, #E10600 0%, #DC143C 100%)',
                  padding: '3px'
                }}>
                  <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_mjlegalvault-1/artifacts/06qw9h5j_tasha.JPG" 
                      alt="Tasha - PopCrown Co-Founder - Michael Jackson fan community leader - King of Pop enthusiast"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: 'center 20%' }}
                    />
                  </div>
                </div>
                <h4 className="text-xl font-bold text-black">Tasha</h4>
                <p className="text-[#DC143C] font-semibold text-sm">Co-Founder</p>
              </div>
            </div>

            {/* Combined Bio */}
            <div className="p-6 rounded-2xl text-center" style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF5F5 100%)',
              border: '2px solid rgba(225, 6, 0, 0.3)',
              boxShadow: '0 10px 40px rgba(0,0,0,0.08)'
            }}>
              <p className="text-gray-700 leading-relaxed mb-4">
                Jo and Tasha are co-founders with a shared backbone: music, grit, and a very low tolerance for nonsense. Both come from dance backgrounds, and both have battled through enough real-life hard times to recognise a fight worth taking on — and to stay fearlessly standing when it gets messy. Tasha has been an MJ fan since she was 4 years old, that's almost 43 years! Jo has always loved the music while simultaneously refusing to give tabloid noise the oxygen; she's never doubted his innocence.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                They became friends in 2014, and that friendship built the foundation for PopCrown: strong morals, high standards, and a track record of walking away from substantial income when it didn't align with their principles. Truth, justice, purpose and principles.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Tasha brings the vision, creative direction, and mission-led storytelling that makes PopCrown feel like a movement, not a "platform." Jo brings the technical leadership, analytical thinking and business discipline that turns big ideas into a stable, scalable product people can actually trust and use. Together, they've poured everything into this — time, energy, late nights, and relentless attention to detail. They're not here to be praised, they're here to get it right and enjoy it with you. PopCrown is what they wanted to see in the world… so they made it.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                PopCrown and its future products and services alongside humanitarian missions will be driven by a clear vision, a dedicated HQ team, and an army of pop icon legacy fans who want to see change.
              </p>
              <div className="mt-6 p-4 rounded-xl" style={{ background: 'rgba(220, 20, 60, 0.1)' }}>
                <p className="text-[#DC143C] font-semibold italic mb-2">
                  To all the dedicated MJ fans who have been fighting for over 30 years…. We hear you and we're done watching this story get rewritten.
                </p>
                <p className="text-[#E10600] font-bold text-lg">
                  In 2026…. We are all bringing Michael Mania back! 👑
                </p>
              </div>
            </div>
          </div>

          {/* Founding Team */}
          <div className="max-w-5xl mx-auto mb-20">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-[#DC143C] mb-3">PopCrown HQ Founding Team</h3>
              <p className="text-gray-600 max-w-3xl mx-auto">
                Meet the women who are in it for the long haul. From peak-era fans with decades of lived experience to research-led truth seekers with sharp media minds — this team brings heart, history, and zero tolerance for nonsense. They moderate, maintain standards, champion truth, and pour everything into making PopCrown a space worthy of Michael's legacy. Diverse backgrounds. United mission. One family.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {[
                { name: 'Angie', bio: "Peak-era MJ experience — she was actually there for the moments Google knows nothing about! From Dangerous and HIStory tour dates to the Oxford Union speech and countless hotel encounters. A walking archive with a law degree and investment banking background, she brings sharp perspective and infectious bubbly energy to our mission: protect authenticity, preserve truth, restore legacy.", photo: 'https://customer-assets.emergentagent.com/job_mjlegalvault-1/artifacts/adr8osgl_Angie.JPG' },
                { name: 'Beth', bio: "Our unapologetically proud lifelong New Yorker! A dedicated MJ fan for 48 years, has seen Michael in concert 4 times, and is PopCrown's TikTok + meme queen. Her MJ adventures have taken her from New York to London to California. By day she's an Independent Living Skills Trainer with 30 years' experience supporting people with disabilities. Fiercely heart-led with a huge memorabilia collection, \"I can still moonwalk perfectly\" energy, and has a Golden Retriever Billiejean — pure PopCrown magic with comedy mic drops. Keep the faith!", photo: 'https://customer-assets.emergentagent.com/job_mjlegalvault-1/artifacts/9lhycqz2_beth.JPG' },
                { name: 'Joanne', bio: "Liverpool-born Joanne is PopCrown HQ to the bone! A dedicated mum & nan with an MJ fan story that starts at age 5. She experienced the peak-era \"video drop\" magic in real time, saw the media shift as it happened, and has been research-led and loyal ever since. A BAD tour attendee bringing authentic perspective, high standards and a big heart for truth, community, and zero tolerance for nonsense.", photo: 'https://customer-assets.emergentagent.com/job_mjlegalvault-1/artifacts/14z7647s_joanne.JPG' },
                { name: 'Rebecca', bio: "Rebecca's MJ journey is the ultimate plot twist — she didn't grow up as a fan! That changed in 2019 after a discredited documentary. As a mum wanting to make informed choices, she went head first down the rabbit hole. With a career in mainstream media and meticulous research skills, what she found left her astounded. Now she's deep in the artistry, bringing sharp standards and unique media knowledge to lead impactful campaigns. Her dedication, contacts and long-term commitment is vital to restoring truth and justice to MJ's legacy.", photo: 'https://customer-assets.emergentagent.com/job_mjlegalvault-1/artifacts/v9u83emm_rebecca.JPG' },
                { name: 'Leanne', bio: "An MJ fan since age 11 (thanks to the Bad album) and absolutely everything we love about this community! She saw Michael on the HIStory tour in '97 and guards that ticket stub like the Crown Jewels. These days she's got a full memorabilia collection and lives for MJ parties — especially Kingvention, where she'll be dancing all night and somehow appear in every photo. Leanne brings big social-butterfly energy, instant warmth, and the kind of hype that makes events feel like home!", photo: 'https://customer-assets.emergentagent.com/job_mjlegalvault-1/artifacts/lgtvhun0_deec3c1c-b077-4990-bc29-3334046762c4.JPG' },
                { name: 'Annie AI', bio: "After years of being asked if she's OK, Annie is absolutely fine — and she's here now to take care of you! PopCrown's tireless digital team member who never sleeps, never complains, and always has time for a chat. She guides fans through the Legal Vault, answers questions at 3am, and knows more MJ facts than Wikipedia. Part assistant, part superfan, 100% here to help. Shamone! ✨", photo: 'https://customer-assets.emergentagent.com/job_mjlegalvault-1/artifacts/a7apjs2h_Screenshot%202026-01-16%20at%2014.42.29.png', isAI: true }
              ].map((member, index) => (
                <div key={index} className="text-center p-4 rounded-xl transition-all hover:transform hover:scale-105" style={{
                  background: '#FFFFFF',
                  border: '1px solid rgba(225, 6, 0, 0.2)',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
                }}>
                  <div className="w-20 h-20 mx-auto mb-3 rounded-full overflow-hidden" style={{
                    background: member.isAI ? 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)' : 'linear-gradient(135deg, #E10600 0%, #DC143C 100%)',
                    padding: '2px'
                  }}>
                    <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      {member.photo ? (
                        <img src={member.photo} alt={`${member.name} - PopCrown Michael Jackson fan community founding team member - MJ fan`} className="w-full h-full object-cover object-top" />
                      ) : member.isAI ? (
                        <Bot className="w-8 h-8 text-white" strokeWidth={1.5} aria-hidden="true" />
                      ) : (
                        <UserIcon className="w-7 h-7 text-gray-400" strokeWidth={1.5} aria-hidden="true" />
                      )}
                    </div>
                  </div>
                  <h4 className="text-lg font-bold text-black mb-1">{member.name}</h4>
                  <p className="text-xs font-semibold mb-2" style={{ color: member.isAI ? '#4ECDC4' : '#E10600' }}>{member.isAI ? 'AI Assistant' : 'Founding Team'}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12" style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 100%)'
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
              Ready to <span className="thriller-text">Join Us</span>?
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              Be part of a community that celebrates what you love, the way it deserves to be celebrated.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/app" className="btn-gold">
                Download the App
              </Link>
              <Link to="/events" className="btn-outline-gold">
                Upcoming Events
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="py-8 bg-gray-100">
        <div className="container mx-auto px-4">
          <p className="text-center text-gray-500 text-sm max-w-3xl mx-auto">
            PopCrown Official Ltd is an independent fan community and editorial platform. 
            Not affiliated with or endorsed by any artist estates, rights holders, or official entities.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
